import {
  InputGroup,
  Intent,
  PopoverPosition,
  Position,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import * as React from 'react';
import { ItemSelector, Tooltip } from 'vet-bones/components';
import { SelectItem, stringToSelectItem } from 'vet-bones/utils/select';

import {
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
} from 'src/constants/testIds';
import { useValidateSensitivePropertyGroupPublishName } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useSensitivePropertyGroups';
import {
  CreationType,
  SensitivePropertyGroupInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { databaseSensitivePropertyGroupsSelector } from 'src/selectors/databaseSensitivePropertyGroups';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export interface SPGRowProps {
  sensitivePropertyGroup: SensitivePropertyGroupInfo;
  handleUpdateSPG: (spg: SensitivePropertyGroupsInfo) => void;
  databaseId: string;
  sensitivePropertyGroups: SensitivePropertyGroupsInfo;
  testId: string;
}

export const SPGRow: React.VFC<SPGRowProps> = ({
  sensitivePropertyGroup,
  handleUpdateSPG,
  databaseId,
  sensitivePropertyGroups,
  testId,
}) => {
  const { spgName, actionType } = sensitivePropertyGroup;

  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const historyPublishName =
    project?.publishHistory.sensitivePropertyGroupsByName[spgName]?.publishName;

  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const databaseSensitivePropertyGroupsByName = useReduxSelector(
    databaseSensitivePropertyGroupsSelector(databaseId)
  );
  const databaseSensitivePropertyGroups: SelectItem[] = Object.keys(
    databaseSensitivePropertyGroupsByName
  ).map(stringToSelectItem);

  const [createName, setCreateName] = React.useState(
    historyPublishName || spgName
  );
  const [replaceAppendName, setReplaceAppendName] = React.useState(
    databaseSensitivePropertyGroupsByName[historyPublishName || spgName]
      ? historyPublishName || spgName
      : ''
  );

  const selectedValue =
    databaseSensitivePropertyGroups.find(
      (spg) => spg.value === replaceAppendName
    ) || null;

  const handleChangeRadioType = (
    e: React.FormEvent<HTMLInputElement>
  ): void => {
    const publishName =
      e.currentTarget.value === CreationType.NEW
        ? createName
        : replaceAppendName;
    const existingGroups = { ...sensitivePropertyGroups };
    const groupToUpdate = {
      ...sensitivePropertyGroup,
      actionType: e.currentTarget.value as CreationType,
      publishName,
    };
    handleUpdateSPG({
      ...existingGroups,
      [groupToUpdate.spgName]: groupToUpdate,
    });
  };

  const handleSelectSPGName = (item: SelectItem | null): void => {
    setReplaceAppendName(item ? item.value : '');
    if (actionType !== CreationType.NEW) {
      const existingGroups = { ...sensitivePropertyGroups };
      const groupToUpdate = {
        ...sensitivePropertyGroup,
      };
      groupToUpdate.publishName = item ? item.value : '';
      handleUpdateSPG({
        ...existingGroups,
        [groupToUpdate.spgName]: groupToUpdate,
      });
    }
  };

  const handleCreateSPGName = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setCreateName(e.target.value);
    if (actionType === CreationType.NEW) {
      const existingGroups = { ...sensitivePropertyGroups };
      const groupToUpdate = {
        ...sensitivePropertyGroup,
      };
      groupToUpdate.publishName = e.target.value;
      handleUpdateSPG({
        ...existingGroups,
        [groupToUpdate.spgName]: groupToUpdate,
      });
    }
  };

  const getSPGInputWarning = useValidateSensitivePropertyGroupPublishName(
    databaseId,
    sensitivePropertyGroups
  );

  const hasExistingGroups = databaseSensitivePropertyGroups.length;
  const replaceAppendTooltip = !hasExistingGroups
    ? copy.components.publishProjectDrawer.modelConfiguration
        .sensitivePropertyGroups.noExisting
    : '';

  return (
    <tr data-testid={testId}>
      <td>{spgName}</td>
      <td>
        <Tooltip
          content={replaceAppendTooltip}
          disabled={!replaceAppendTooltip}
          position={PopoverPosition.TOP}
        >
          <RadioGroup
            inline
            onChange={handleChangeRadioType}
            selectedValue={actionType}
          >
            <Radio
              data-testid={
                TEST_IDS.publishProjectDrawer.model.sensitivePropertyGroups
                  .create
              }
              label="Create"
              value={CreationType.NEW}
            />
            <Radio
              data-testid={
                TEST_IDS.publishProjectDrawer.model.sensitivePropertyGroups
                  .replace
              }
              disabled={!hasExistingGroups}
              inline
              label="Replace"
              value={CreationType.REPLACE}
            />
            <Radio
              data-testid={
                TEST_IDS.publishProjectDrawer.model.sensitivePropertyGroups
                  .append
              }
              disabled={!hasExistingGroups}
              inline
              label="Append"
              value={CreationType.APPEND}
            />
          </RadioGroup>
        </Tooltip>
        <div className="sd-drawer-selector-menu">
          {actionType === CreationType.NEW ? (
            <div
              onMouseEnter={() => setIsTooltipOpen(true)}
              onMouseLeave={() => setIsTooltipOpen(false)}
            >
              <InputGroup
                data-testid={
                  TEST_IDS.publishProjectDrawer.model.sensitivePropertyGroups
                    .textInput
                }
                intent={
                  getSPGInputWarning(sensitivePropertyGroup)
                    ? Intent.DANGER
                    : Intent.NONE
                }
                onChange={handleCreateSPGName}
                placeholder={
                  copy.components.publishProjectDrawer.modelConfiguration
                    .sensitivePropertyGroups.newPlaceholder
                }
                value={createName}
              />
              <Tooltip
                content={getSPGInputWarning(sensitivePropertyGroup) || ''}
                disabled={!getSPGInputWarning(sensitivePropertyGroup)}
                isOpen={isTooltipOpen}
                position={Position.BOTTOM}
              />
            </div>
          ) : (
            <ItemSelector.Single
              buttonText={
                selectedValue?.text ||
                copy.components.publishProjectDrawer.modelConfiguration
                  .sensitivePropertyGroups.existingPlaceholder
              }
              filterText={copy.components.defaults.filter}
              items={databaseSensitivePropertyGroups}
              itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
              onSelect={handleSelectSPGName}
              selected={selectedValue}
              testId={
                TEST_IDS.publishProjectDrawer.model.sensitivePropertyGroups
                  .select
              }
            />
          )}
        </div>
      </td>
    </tr>
  );
};
